class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      "keyup",
      (evt) => evt.code === "Escape" && this.close()
    );
    this.querySelector("#CartDrawer-Overlay").addEventListener(
      "click",
      this.close.bind(this)
    );
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLinks = document.querySelectorAll("#cart-icon-bubble, #cart-icon-bubble-sticky");
    if (!cartLinks.length) return;
		cartLinks.forEach(icon => this.addDrawerOpenHandler(icon));
  }

  addDrawerOpenHandler(icon) {
    if (!icon) return;

    icon.setAttribute("role", "button");
    icon.setAttribute("aria-haspopup", "dialog");
    icon.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(icon);
    });
    icon.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(icon);
      }
    });
  }

  showLoadingState() {
    this.classList.add('is-adding-item');
    this.querySelector("#CartDrawer-Overlay")?.addEventListener(
      "click",
      this.close.bind(this)
    );
    this.open();
  }

  hideLoadingState() {
    this.classList.remove('is-adding-item');
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
      this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add("animate", "active");
    });

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this.classList.contains("is-empty")
          ? this.querySelector(".drawer__inner-empty")
          : document.getElementById("CartDrawer");
        const focusElement =
          this.querySelector(".drawer__inner") ||
          this.querySelector(".drawer__close");
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add("overflow-hidden");
  }

  close() {
    this.classList.remove("active");
    removeTrapFocus(this.activeElement);
    document.body.classList.remove("overflow-hidden");

    if (MOUSE_CURSOR) MOUSE_CURSOR.hide();
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute(
        "aria-controls",
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details").hasAttribute("open")
      );
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.hideLoadingState();
    const drawerInner = this.querySelector(".drawer__inner");
    if (drawerInner && drawerInner.classList.contains("is-empty")) {
      drawerInner.classList.remove("is-empty");
    }
    if (this.classList.contains("is-empty")) {
      this.classList.remove("is-empty");
    }

    if (parsedState && parsedState.id) {
      this.productId = parsedState.id;
    }
		
    if (parsedState && typeof parsedState.item_count !== 'undefined') {
      this.updateCartIconBubbles(parsedState.item_count);
    }

    if (parsedState && parsedState.sections) {
      const parser = new DOMParser();
      this.getSectionsToRender()?.forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
        const sectionHtml = parsedState.sections[section.id];
        if (!sectionElement || !sectionHtml) return;

        const doc = parser.parseFromString(sectionHtml, "text/html");
        const newElement = section.selector
          ? doc.querySelector(section.selector)
          : (doc.getElementById(section.id) || doc.querySelector(".shopify-section") || doc.body.firstElementChild);

        if (newElement) {
          sectionElement.innerHTML = newElement.innerHTML;
        } else if (doc.body) {
          sectionElement.innerHTML = doc.body.innerHTML;
        }
      });
    }

    this.querySelector("#CartDrawer-Overlay")?.addEventListener(
      "click",
      this.close.bind(this)
    );
    this.open();
  }

  updateCartIconBubbles(count) {
    const bubbles = document.querySelectorAll('.cart-count-bubble');
    bubbles.forEach((bubble) => {
      const numSpan = bubble.querySelector('[aria-hidden="true"]');
      if (numSpan) {
        numSpan.textContent = count <= 100 ? count : '100+';
      }
    });

    if (count > 0) {
      const headerCartIcon = document.querySelector('#cart-icon-bubble');
      if (headerCartIcon && !headerCartIcon.querySelector('.cart-count-bubble')) {
        const bubble = document.createElement('div');
        bubble.className = 'cart-count-bubble';
        bubble.innerHTML = `<span aria-hidden="true">${count}</span><span class="visually-hidden">Cart count: ${count}</span>`;
        headerCartIcon.appendChild(bubble);
      }
      const stickyCartIcon = document.querySelector('#cart-icon-bubble-sticky .cart-count');
      if (stickyCartIcon && !stickyCartIcon.querySelector('.cart-count-bubble')) {
        const bubble = document.createElement('span');
        bubble.id = 'cart-count-bubble-sticky';
        bubble.className = 'cart-count-bubble cart-count-bubble-sticky';
        bubble.innerHTML = `<span aria-hidden="true">${count}</span><span class="visually-hidden">Cart count: ${count}</span>`;
        stickyCartIcon.appendChild(bubble);
      }
    } else if (count === 0) {
      document.querySelectorAll('.cart-count-bubble').forEach((el) => el.remove());
    }
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector)?.innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
      {
        id: "cart-icon-bubble-sticky",
        section: "cart-icon-bubble-sticky",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);
